import { isOfficiallyRecused } from "@client/utils/interest";
import { isRichTextBlank } from "@client/utils/isRichTextBlank";
import {
  getActiveProposals,
  getEvaluableRequestBase,
  getUnevaluatedEvaluableGroups,
} from "@engine/round/accessors";
import {
  type EvaluationState,
  type Proposal,
  type Recommender,
  type RoundAggregate,
  type RoundMember,
} from "@engine/round/aggregate";
import {
  getInvestigation,
  getProposalDisclosure,
  getReviewProgress,
} from "@engine/round/proposalMemberData";
import { type Evaluable, type Track } from "@lib/types";
import { type EvaluationRow } from "./evaluationRows.types";

/** Get tracks array from proposal typed fields. */
function getProposalTracks(proposal: Proposal): Track[] {
  const tracks: Track[] = [];

  if (proposal.typedFields.targetsFreedom) {
    tracks.push("FREEDOM");
  }

  if (proposal.typedFields.targetsFairness) {
    tracks.push("FAIRNESS");
  }

  return tracks;
}

/** Build evaluation rows from round data. */
export function buildEvaluationRows(
  round: RoundAggregate,
  recommender: Recommender,
  memberId: string,
  viewerMember: RoundMember | undefined,
): EvaluationRow[] {
  const groups = getUnevaluatedEvaluableGroups(round, recommender);

  const rows: EvaluationRow[] = [];

  const getMemberData = (proposalId: string) => {
    const disclosure = getProposalDisclosure(round, proposalId, memberId);
    return {
      investigationInterest:
        getInvestigation(round, proposalId, memberId)?.interest ?? null,
      reviewProgress: getReviewProgress(round, proposalId, memberId) ?? null,
      isRecused: isOfficiallyRecused(disclosure),
    };
  };

  const addFromGroup = (
    proposals: Evaluable[],
    groupStatus: EvaluationState | null,
  ) => {
    for (const evaluable of proposals) {
      if (evaluable.type !== "proposal") {
        throw new Error(
          `Expected Proposal in evaluation group, got ${evaluable.type}`,
        );
      }

      const proposal = evaluable;

      if (proposal.id === round.holdProposalId) {
        continue;
      }

      const evaluation = recommender.evaluations[proposal.id];
      // Hidden is independent of evaluation state; visible groups supply their
      // normalized state (including zeroed curves as evaluated).
      const status = groupStatus ?? evaluation?.state ?? "not-started";
      rows.push({
        proposalId: proposal.id,
        shortname: proposal.shortname,
        longname: proposal.longname,
        tracks: getProposalTracks(proposal),
        baseRequest: getEvaluableRequestBase(round, proposal.id),
        status,
        engineState: status,
        finality: evaluation?.finality ?? null,
        negativeValue: evaluation?.negativeValue === true,
        lastTouched: evaluation?.lastTouchedAt ?? null,
        ...getMemberData(proposal.id),
        hasViewerNotes: hasSavedNote(viewerMember, proposal.id),
        proposal,
        ...(groupStatus == null ? { hidden: true as const } : {}),
      });
    }
  };

  addFromGroup(groups.evaluated, "evaluated");
  addFromGroup(groups.zeroed, "evaluated");
  addFromGroup(groups.deferred, "deferred");
  addFromGroup(groups.notStarted, "not-started");
  addFromGroup(groups.recused, "recused");
  addFromGroup(groups.canceled, "canceled");

  addFromGroup(groups.hidden, null);

  return rows;
}

/** Build proposal rows for non-recommenders. */
export function buildProposalRows(
  round: RoundAggregate,
  viewerMember: RoundMember | undefined,
): EvaluationRow[] {
  return getActiveProposals(round)
    .filter((proposal) => proposal.id !== round.holdProposalId)
    .map((proposal) => ({
      proposalId: proposal.id,
      shortname: proposal.shortname,
      longname: proposal.longname,
      tracks: getProposalTracks(proposal),
      baseRequest: getEvaluableRequestBase(round, proposal.id),
      status: "not-started" as const,
      engineState: "not-started" as const,
      // Non-recommender viewers have no evaluation of their own to annotate;
      // they read recommenders' verdicts on the proposal page instead.
      finality: null,
      negativeValue: false,
      lastTouched: null,
      investigationInterest: null,
      reviewProgress: null,
      isRecused: false,
      hasViewerNotes: hasSavedNote(viewerMember, proposal.id),
      proposal,
    }));
}

function hasSavedNote(
  member: RoundMember | undefined,
  proposalId: string,
): boolean {
  return !isRichTextBlank(member?.evaluableNotes[proposalId]);
}
